import React, { useEffect, useMemo, useState } from "react";
import "./TemplateManagerModal.css";
import CreateTemplateModal from "../CreateTemplateModal/CreateTemplateModal";

const PAGE_SIZE = 5;

export default function TemplateManagerModal({ open, onClose }) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  const [editorOpen, setEditorOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);

  const fetchTemplates = async () => {
    setLoading(true);
    try {
      const res = await fetch("https://api.ocr.cybertech.com.vn/packages");
      const data = await res.json();
      if (Array.isArray(data)) setTemplates(data);
    } catch (err) {
      console.error("Fetch templates failed", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!open) return;
    fetchTemplates();
    setSearch("");
    setPage(1);
  }, [open]);

  useEffect(() => {
    setPage(1);
  }, [search]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return templates;
    return templates.filter((t) => `${t.name} ${t.sample}`.toLowerCase().includes(term));
  }, [templates, search]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const sliceStart = (currentPage - 1) * PAGE_SIZE;
  const paginated = filtered.slice(sliceStart, sliceStart + PAGE_SIZE);

  const handleDelete = async (tpl) => {
    const confirmDelete = window.confirm(`Xóa template "${tpl.name}"?`);
    if (!confirmDelete) return;
    setLoading(true);
    try {
      const res = await fetch(`https://api.ocr.cybertech.com.vn/packages/${tpl._id}`, { method: "DELETE" });
      if (res.ok) {
        setTemplates((prev) => prev.filter((t) => t._id !== tpl._id));
      }
    } catch (err) {
      console.error("Delete template failed", err);
    } finally {
      setLoading(false);
    }
  };

  const handleSaved = (tpl) => {
    if (!tpl?._id) return;
    setTemplates((prev) => {
      const exists = prev.some((t) => t._id === tpl._id);
      if (exists) return prev.map((t) => (t._id === tpl._id ? tpl : t));
      return [tpl, ...prev];
    });
    setEditorOpen(false);
    setEditingTemplate(null);
  };

  const openCreate = () => {
    setEditingTemplate(null);
    setEditorOpen(true);
  };

  const openEdit = (tpl) => {
    setEditingTemplate(tpl);
    setEditorOpen(true);
  };

  if (!open) return null;

  return (
    <>
      <div className="tm-overlay">
        <div className="tm-box">
          <div className="tm-header">
            <div>
              <h3>Quản lý template</h3>
              <p className="tm-subtitle">Danh sách gọn gàng, kèm tìm kiếm và phân trang.</p>
            </div>
            <button className="tm-close" onClick={onClose}>×</button>
          </div>

          <div className="tm-controls">
            <input
              className="tm-search"
              placeholder="Tìm theo tên hoặc sample"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <div className="tm-actions">
              <button className="tm-btn" onClick={fetchTemplates} disabled={loading}>Tải lại</button>
              <button className="tm-btn primary" onClick={openCreate}>+ Tạo template</button>
            </div>
          </div>

          <div className="tm-table">
            <div className="tm-row tm-head">
              <div className="col name">Tên</div>
              <div className="col sample">Sample</div>
              <div className="col count">Fields</div>
              <div className="col actions">Hành động</div>
            </div>
            {paginated.map((tpl) => (
              <div className="tm-row" key={tpl._id}>
                <div className="col name">{tpl.name}</div>
                <div className="col sample">{tpl.sample}</div>
                <div className="col count">{tpl.indexes?.length || 0}</div>
                <div className="col actions">
                  <button className="tm-link" onClick={() => openEdit(tpl)}>Sửa</button>
                  <button className="tm-link danger" onClick={() => handleDelete(tpl)}>Xóa</button>
                </div>
              </div>
            ))}
            {paginated.length === 0 && (
              <div className="tm-empty">Không có template phù hợp.</div>
            )}
          </div>

          <div className="tm-footer">
            <div className="tm-pagination">
              <button
                className="tm-btn"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
              >
                Trước
              </button>
              <span className="tm-page">Trang {currentPage}/{totalPages}</span>
              <button
                className="tm-btn"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
              >
                Sau
              </button>
            </div>
            {loading && <div className="tm-loading">Đang tải...</div>}
          </div>
        </div>
      </div>

      <CreateTemplateModal
        open={editorOpen}
        onClose={() => setEditorOpen(false)}
        onSaved={handleSaved}
        initialTemplate={editingTemplate}
        mode={editingTemplate ? "edit" : "create"}
      />
    </>
  );
}
